import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { Menu, MenuModule } from 'primeng/menu';
import { DrawerModule } from 'primeng/drawer';
import type { MenuItem } from 'primeng/api';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { httpErrorApiMessage } from '../../core/api-response-utils';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { AuthService } from '../../services/auth.service';
import { CustomerService } from '../../services/customer.service';
import type { CustomerDTO, MitMandateDTO, MitMandateStatusValue } from '../../core/models';
import {
  MIT_MANDATE_STATUS_LABELS,
  MitService,
  mitMandateActions,
  mitMandateStatusSeverity
} from '../../services/mit.service';
import { MitMandateStatusAction, MitMandateStatusDialogComponent } from './mit-mandate-status-dialog.component';
import { MitMandateSummaryComponent } from './mit-mandate-summary.component';

type LabeledCustomer = CustomerDTO & { displayLabel: string };

@Component({
  selector: 'app-mit-mandates-list',
  templateUrl: './mit-mandates-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .mit-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        position: sticky;
        top: 0;
        z-index: 20;
      }
      :host ::ng-deep .mit-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .mit-table .mit-col-validity {
        width: 12rem;
        max-width: 12rem;
      }
      :host ::ng-deep p-table,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .mit-mandate-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .mit-mandate-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    PERMISSION_UI_DIRECTIVES,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    SelectModule,
    InputTextModule,
    TagModule,
    TooltipModule,
    AutoCompleteModule,
    DatePickerModule,
    MenuModule,
    DrawerModule,
    MatIconModule,
    MitMandateStatusDialogComponent,
    MitMandateSummaryComponent
  ]
})
export class MitMandatesListComponent {
  private mitService = inject(MitService);
  private customerService = inject(CustomerService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private customerQuery = new Subject<string>();
  private rowMenu = viewChild<Menu>('rowMenu');

  readonly canExportMandates = this.auth.hasRole('MIT_MANDATE_EXPORT');
  exporting = signal(false);

  get exportButtonTooltip(): string {
    if (!this.canExportMandates) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    if (this.totalItems() === 0) {
      return 'No hay mandatos para exportar con los filtros actuales';
    }
    return 'Descargar todos los mandatos filtrados en CSV';
  }

  mandates = signal<MitMandateDTO[]>([]);
  loading = signal(true);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  totalItems = signal(0);
  currentPage = signal(0);
  pageSize = signal(20);
  sortField = signal('createdAt');
  sortOrder = signal(-1);
  hasActiveFilters = signal(false);
  customerOptions = signal<LabeledCustomer[]>([]);
  rowMenuItems = signal<MenuItem[]>([]);
  statusMandate = signal<MitMandateDTO | null>(null);
  statusAction = signal<MitMandateStatusAction | null>(null);
  selectedMandate = signal<MitMandateDTO | null>(null);
  isDetailsOpen = signal(false);
  detailsLoading = signal(false);

  readonly pageSizeOptions = [10, 20, 50];
  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly statusOptions = Object.entries(MIT_MANDATE_STATUS_LABELS).map(([value, label]) => ({ value, label }));

  filtersForm = this.fb.group({
    q: '',
    status: '',
    customer: this.fb.control<LabeledCustomer | string | null>(null),
    externalReference: '',
    createdAtRange: this.fb.control<Date[] | null>(null)
  });

  constructor() {
    this.customerQuery.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed()).subscribe((query) => {
      this.customerService.listCustomers({
        'legalName[lk]': query,
        'comercialName[lk]': query,
        'taxId[lk]': query
      }, { page: 0, size: 20 }).subscribe((response) => {
        this.customerOptions.set((response.body ?? []).map((customer) => ({
          ...customer,
          displayLabel: customer.comercialName || customer.legalName || customer.taxId || ''
        })));
      });
    });
    for (const control of [this.filtersForm.controls.q, this.filtersForm.controls.externalReference]) {
      control.valueChanges.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed())
        .subscribe(() => this.applyFilters());
    }
    this.filtersForm.controls.status.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.filtersForm.controls.createdAtRange.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.load();
  }

  onCustomerSearch(event: { query: string }): void {
    const query = (event.query ?? '').trim();
    if (query.length < 3) {
      this.customerOptions.set([]);
      return;
    }
    this.customerQuery.next(query);
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    this.mitService.search(this.buildSpec(), {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    }).subscribe({
      next: (response) => {
        this.mandates.set(response.body ?? []);
        this.totalItems.set(response.page?.totalItems ?? response.body?.length ?? 0);
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: () => {
        this.error.set('No se pudieron cargar los mandatos.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows || this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged = nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged ? 0 : event.rows ? Math.floor((event.first ?? 0) / event.rows) : 0;
    if (!sortChanged && page === this.currentPage() && size === this.pageSize()) {
      return;
    }
    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.load();
  }

  applyFilters(): void {
    this.hasActiveFilters.set(Object.keys(this.buildSpec()).length > 0);
    this.currentPage.set(0);
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({ q: '', status: '', customer: null, externalReference: '', createdAtRange: null }, { emitEvent: false });
    this.customerOptions.set([]);
    this.applyFilters();
  }

  exportCsv(): void {
    if (!this.canExportMandates || this.exporting()) return;
    this.exporting.set(true);
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    this.mitService.exportCsv(this.buildSpec(), [`${this.sortField()},${direction}`]).subscribe({
      next: ({ blob, filename }) => {
        this.triggerBrowserDownload(blob, filename);
        this.exporting.set(false);
        this.toastr.success('CSV de mandatos descargado');
      },
      error: (err: HttpErrorResponse) => {
        this.exporting.set(false);
        const fallback = 'No se pudo exportar el CSV de mandatos';
        if (err.error instanceof Blob) {
          err.error.text().then((text) => {
            try {
              const json = JSON.parse(text) as { message?: string };
              this.toastr.error(json?.message?.trim() || fallback);
            } catch {
              this.toastr.error(fallback);
            }
          });
          return;
        }
        this.toastr.error(httpErrorApiMessage(err, fallback));
      }
    });
  }

  open(mandate: MitMandateDTO): void {
    void this.router.navigate(['/mit-mandates', mandate.id]);
  }

  openDetails(mandate: MitMandateDTO): void {
    if (!mandate?.id) return;
    this.isDetailsOpen.set(true);
    this.selectedMandate.set(null);
    this.detailsLoading.set(true);
    this.mitService.getById(mandate.id).subscribe({
      next: (response) => {
        this.selectedMandate.set(response.body ?? null);
        this.detailsLoading.set(false);
      },
      error: (err) => {
        this.detailsLoading.set(false);
        this.isDetailsOpen.set(false);
        this.toastr.error(httpErrorApiMessage(err, 'No se pudo cargar el mandato'));
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedMandate.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  openFromDrawer(): void {
    const mandate = this.selectedMandate();
    if (!mandate) return;
    this.closeDetails();
    this.open(mandate);
  }

  openRowMenu(event: Event, mandate: MitMandateDTO): void {
    event.stopPropagation();
    const actions = mitMandateActions(mandate, this.auth.hasRole('MIT_CONFIG_WRITE'));
    const canWrite = this.auth.hasRole('MIT_MANDATE_WRITE');
    const items: MenuItem[] = [
      { label: 'Ver detalle', icon: 'pi pi-eye', command: () => this.openDetails(mandate) },
      { label: 'Abrir mandato', icon: 'pi pi-external-link', command: () => this.open(mandate) }
    ];
    if (actions.charge && this.auth.hasRole('MIT_TRANSACTION_WRITE')) {
      items.push({
        label: 'Cobrar',
        icon: 'pi pi-credit-card',
        command: () => void this.router.navigate(['/mit-mandates', mandate.id], { queryParams: { accion: 'cobrar' } })
      });
    }
    if (canWrite && actions.suspend) {
      items.push({ label: 'Suspender', icon: 'pi pi-pause', command: () => this.openStatus(mandate, 'suspend') });
    }
    if (canWrite && actions.reactivate) {
      items.push({ label: 'Reactivar', icon: 'pi pi-play', command: () => this.openStatus(mandate, 'reactivate') });
    }
    if (canWrite && actions.revoke) {
      items.push({ label: 'Revocar', icon: 'pi pi-ban', styleClass: 'text-red-600', command: () => this.openStatus(mandate, 'revoke') });
    }
    this.rowMenuItems.set(items);
    this.rowMenu()?.toggle(event);
  }

  openStatus(mandate: MitMandateDTO, action: MitMandateStatusAction): void {
    this.statusMandate.set(mandate);
    this.statusAction.set(action);
  }

  closeStatus(): void {
    this.statusAction.set(null);
    this.statusMandate.set(null);
  }

  onStatusChanged(updated: MitMandateDTO): void {
    this.mandates.update((rows) => rows.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
    this.selectedMandate.update((current) =>
      current?.id === updated.id ? { ...current, ...updated, consents: updated.consents ?? current.consents } : current
    );
  }

  statusLabel(status: MitMandateStatusValue | undefined): string {
    return status ? MIT_MANDATE_STATUS_LABELS[status] ?? status : '—';
  }

  statusSeverity = mitMandateStatusSeverity;

  /**
   * Avance de la vigencia del mandato: verde 0–70 %, amarillo 71–99 %, rojo 100 %.
   * Sin fecha de fin la barra queda vacía; revocado o vencido por otra causa, en gris.
   */
  mandateValidity(mandate: MitMandateDTO): {
    start: Date;
    end: Date | null;
    percent: number;
    barClass: string;
    expired: boolean;
    remainingLabel: string;
  } | null {
    const startRaw = mandate.validFrom ?? mandate.activatedAt ?? mandate.createdAt;
    if (!startRaw) return null;
    const start = new Date(startRaw);
    const end = mandate.validUntil ? new Date(mandate.validUntil) : null;
    const closed = mandate.status === 'REVOKED'
      || (mandate.status === 'EXPIRED' && mandate.expiredReason !== 'VALIDITY_ENDED');
    const now = Date.now();
    if (!end) {
      return {
        start,
        end,
        percent: 0,
        barClass: 'bg-slate-400',
        expired: false,
        remainingLabel: now < start.getTime() ? `Inicia ${this.relativeDays(start.getTime() - now)}` : 'Vigencia sin fecha de fin'
      };
    }
    const span = end.getTime() - start.getTime();
    let percent: number;
    if (now >= end.getTime()) {
      percent = 100;
    } else if (now <= start.getTime() || span <= 0) {
      percent = 0;
    } else {
      percent = Math.min(99, Math.floor(((now - start.getTime()) / span) * 100));
    }
    const expired = !closed && percent >= 100;
    const barClass = closed ? 'bg-slate-400' : percent >= 100 ? 'bg-red-500' : percent > 70 ? 'bg-amber-400' : 'bg-emerald-500';
    let remainingLabel: string;
    if (closed) {
      remainingLabel = this.statusLabel(mandate.status);
    } else if (now < start.getTime()) {
      remainingLabel = `Inicia ${this.relativeDays(start.getTime() - now)}`;
    } else if (now >= end.getTime()) {
      const days = Math.floor((now - end.getTime()) / 86_400_000);
      remainingLabel = days === 0 ? 'Venció hoy' : `Venció hace ${days === 1 ? '1 día' : `${days} días`}`;
    } else {
      remainingLabel = `Vence ${this.relativeDays(end.getTime() - now)}`;
    }
    return { start, end, percent, barClass, expired, remainingLabel };
  }

  private relativeDays(ms: number): string {
    const days = Math.floor(ms / 86_400_000);
    if (days === 0) return 'hoy';
    return days === 1 ? 'en 1 día' : `en ${days} días`;
  }

  statusIconClasses(status: MitMandateStatusValue | undefined): string {
    const base = 'shrink-0 h-8 w-8 rounded-full flex items-center justify-center';
    if (status === 'ACTIVE') return `${base} bg-emerald-100 text-emerald-600`;
    if (status === 'PENDING_ENROLLMENT') return `${base} bg-sky-100 text-sky-600`;
    if (status === 'SUSPENDED') return `${base} bg-amber-100 text-amber-600`;
    if (status === 'REVOKED') return `${base} bg-red-100 text-red-600`;
    return `${base} bg-slate-200 text-slate-600`;
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'mandatos.csv';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private buildSpec(): Record<string, string> {
    const raw = this.filtersForm.getRawValue();
    const spec: Record<string, string> = {};
    const q = raw.q?.trim();
    if (q) spec['q[lk]'] = q;
    if (raw.status) spec['status'] = raw.status;
    const customer = raw.customer;
    if (customer && typeof customer !== 'string' && customer.id != null) spec['customerId'] = String(customer.id);
    const reference = raw.externalReference?.trim();
    if (reference) spec['externalReference'] = reference;
    const range = raw.createdAtRange;
    if (range?.[0] && range?.[1]) {
      const from = new Date(range[0]);
      from.setHours(0, 0, 0, 0);
      const toExclusive = new Date(range[1]);
      toExclusive.setHours(0, 0, 0, 0);
      toExclusive.setDate(toExclusive.getDate() + 1);
      spec['createdAt[gte]'] = from.toISOString();
      spec['createdAt[lt]'] = toExclusive.toISOString();
    }
    return spec;
  }
}
